import { Trans, t } from '@lingui/macro';
import { useMemo, useState } from 'react';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import { enUS, fr } from 'react-day-picker/locale';
import { useLingui } from '@lingui/react';

import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';

const selectSurfaceClass =
    'box-border h-9 w-full cursor-pointer appearance-none rounded-md border border-solid border-[hsl(var(--border))] bg-[hsl(var(--input))] py-0 pl-3 pr-10 text-sm text-foreground shadow-sm focus:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function ShortDatePickerContent({ value, onChange, onClose }) {
    const { i18n } = useLingui();
    const current = value && dayjs(value).isValid() ? dayjs(value) : dayjs().startOf('month');
    const endYear = new Date().getFullYear() + 150;
    const startYear = new Date().getFullYear() - 150;

    const months = useMemo(
        () =>
            Array.from({ length: 12 }, (_, monthIndex) => ({
                value: monthIndex,
                label: dayjs().month(monthIndex).locale(i18n.locale).format('MMMM'),
            })),
        [i18n.locale],
    );

    const years = useMemo(() => {
        const options = [];
        for (let year = endYear; year >= startYear; year -= 1) {
            options.push(year);
        }
        return options;
    }, [endYear, startYear]);

    const handleMonthChange = (event) => {
        onChange(current.month(Number(event.target.value)).startOf('month'));
    };

    const handleYearChange = (event) => {
        onChange(current.year(Number(event.target.value)).startOf('month'));
    };

    return (
        <div data-short-date-picker className="flex min-w-[240px] gap-2 p-3">
            <div className="relative flex-1">
                <select
                    aria-label={t`Month`}
                    value={current.month()}
                    onChange={handleMonthChange}
                    className={selectSurfaceClass}
                >
                    {months.map((month) => (
                        <option key={month.value} value={month.value}>
                            {month.label}
                        </option>
                    ))}
                </select>
                <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
            </div>
            <div className="relative w-[100px]">
                <select
                    aria-label={t`Year`}
                    value={current.year()}
                    onChange={handleYearChange}
                    className={selectSurfaceClass}
                >
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
                <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
            </div>
            <Button onClick={onClose}>
                <Trans>Close</Trans>
            </Button>
        </div>
    );
}

export function RegistrationDateField({
    value,
    mode,
    error,
    errorId,
    questionId,
    required,
    placeholder,
    onChange,
}) {
    const [open, setOpen] = useState(false);
    const { i18n } = useLingui();

    const selectedDate = value && dayjs(value).isValid() ? dayjs(value).toDate() : undefined;
    const displayValue =
        selectedDate && mode === 'full'
            ? dayjs(selectedDate).format('YYYY-MM-DD')
            : selectedDate && mode === 'short'
              ? dayjs(selectedDate).format('YYYY-MM')
              : null;

    const handleSelect = (date) => {
        if (!date) {
            return;
        }

        onChange(dayjs(date).startOf('day'));
        setOpen(false);
    };

    const handleShortDateChange = (nextValue) => {
        onChange(nextValue);
    };

    return (
        <div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        aria-labelledby={questionId}
                        aria-describedby={errorId}
                        aria-required={required}
                        aria-invalid={Boolean(error)}
                        className={cn(
                            'h-9 w-full justify-start border-[hsl(var(--border))] bg-[hsl(var(--input))] px-3 text-left font-normal text-foreground hover:bg-[hsl(var(--input))]',
                            !displayValue && 'text-muted-foreground',
                            error && 'border-destructive',
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                        {displayValue || placeholder}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-auto p-0"
                    align="start"
                    onFocusOutside={
                        mode === 'short' ? (event) => event.preventDefault() : undefined
                    }
                >
                    {mode === 'short' ? (
                        <ShortDatePickerContent
                            value={value}
                            onChange={handleShortDateChange}
                            onClose={() => setOpen(false)}
                        />
                    ) : (
                        <Calendar
                            locale={i18n.locale === 'fr' ? fr : enUS}
                            formatters={{
                                formatMonthDropdown: (date) =>
                                    dayjs(date).locale(i18n.locale).format('MMM'),
                            }}
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleSelect}
                            captionLayout="dropdown"
                            defaultMonth={selectedDate}
                        />
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
}
